import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GatewayBrand, GatewayShell, GatewaySpinner } from '@/_shared/components/GatewayShell';
import { Callout } from '@/_shared/components/ui/callout';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { clearReturnAfterSignOut, readReturnAfterSignOut } from '@/_shared/lib/return-after-sign-out';

import { SignInErrorCallout } from '../components/SignInErrorCallout';
import { SignInProviders } from '../components/SignInProviders';
import { useSharedSessionApi, useSharedSessionStart } from '../hooks/useSharedSession';
import { useSignInError } from '../hooks/useSignInError';
import { WEB_APP_ENTRY, webAppEntryReturnTo } from '../lib/web-app-entry';

export function Login(): React.ReactElement {
  usePageTitle('Login');
  const [searchParams] = useSearchParams();
  const signInError = useSignInError();
  const rawError = signInError.code;
  // A page sends a signed-out user here after the session ended. It keeps the page in `return_to`.
  const sessionEnded = searchParams.get('reason') === 'session_ended';
  // A visit from the web app's sidebar signs in here with Lookout's own sign-in (ACT2-871). It arrives as
  // `from=rudderstack` with campaign keys, or as `entry` from the old OAuth start. A retry after an error does not count.
  const webAppReturnTo = webAppEntryReturnTo(searchParams.toString());
  const fromWebApp = webAppReturnTo !== null || (searchParams.get('entry') === WEB_APP_ENTRY && !rawError);
  // A Google or SSO sign-out comes back here through the provider's page, without the target it had.
  const [returnAfterSignOut] = useState(readReturnAfterSignOut);
  useEffect(() => clearReturnAfterSignOut(), []);
  const returnTo = webAppReturnTo ?? searchParams.get('return_to') ?? returnAfterSignOut;
  // A retry after a failed invite sign-in is still an invite sign-in, so no RudderStack organization is created.
  const invite = searchParams.get('invite') === '1' || (returnTo?.startsWith('/invite/') ?? false);
  // ACT2-879: a session that config-backend already has signs in here with no prompt. A page with an error shows it.
  const sharedStart = useSharedSessionStart({ returnTo, enabled: !rawError, fromWebApp });
  // Until the page knows whether a silent start runs, it shows no "session ended" message: the start can sign in.
  const { loading: providersLoading } = useSharedSessionApi();

  if (sharedStart.checking) return <GatewaySpinner mode="fullScreen" />;

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />

      {signInError.message && <SignInErrorCallout message={signInError.message} className="mb-6" />}
      {sessionEnded && !rawError && !providersLoading && (
        <Callout variant="info" role="status" className="mb-6 text-left">
          Your session ended. Sign in again.
        </Callout>
      )}

      <SignInProviders returnTo={returnTo} invite={invite} fromWebApp={fromWebApp} onNewAttempt={signInError.dismiss} />
    </GatewayShell>
  );
}
